import aa from 'search-insights';
import { IS_SEARCH } from 'helpers/constants/algolia';
// Maximum number of items to send to Algolia at once (to avoid API limits)
const chunkSize = 20;

type AlgoliaInsightsMethod = 'purchasedObjectIDsAfterSearch' | 'purchasedObjectIDs';

export class AlgoliaAnalytics {
  locale: string;
  index: string | undefined;

  constructor(locale: string) {
    this.locale = locale;
    this.index =
      locale === 'en_GB'
        ? process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US
        : process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE;

    this.initialize();
  }

  private initialize() {
    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('search-insights no longer validates event payloads')) {
        return;
      }
      originalWarn(...args);
    };

    aa('init', {
      appId: process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID!,
      apiKey: process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY!,
    });
  }

  handleProductClick(productId: string | undefined, queryId: string | undefined, position: number | undefined) {
    sessionStorage?.removeItem(IS_SEARCH);
    if (!productId || !queryId || !position) return;
    aa('clickedObjectIDsAfterSearch', {
      eventName: 'PLP Product Click',
      index: this?.index!,
      objectIDs: [productId],
      queryID: queryId,
      positions: [position],
    });
  }

  handleFilterClick(userToken: string, filters: string[]) {
    for (let i = 0; i < filters?.length; i += chunkSize) {
      const chunk = filters?.slice(i, i + chunkSize);
      aa('clickedFilters', {
        userToken: userToken?.replace(/^"|"$/g, ''),
        index: this?.index!,
        eventName: 'Facets Clicked',
        filters: chunk,
      });
    }
  }

  handleClickAfterSearch(
    productId: string[],
    userToken: string,
    queryId: string,
    position: number[],
    isAutoCompleteSearch?: boolean,
  ) {
    sessionStorage?.setItem(IS_SEARCH, 'true');
    for (let i = 0; i < productId?.length; i += chunkSize) {
      const chunk = productId?.slice(i, i + chunkSize);
      aa('clickedObjectIDsAfterSearch', {
        userToken: userToken?.replace(/^"|"$/g, ''),
        index: this?.index!,
        eventName: isAutoCompleteSearch ? 'Search: Product Clicked' : 'SRP: Product Clicked',
        queryID: queryId,
        objectIDs: chunk,
        positions: position,
      });
    }
  }
  handleColourSwatchesClickedAfterSearch(productId: string[], userToken: string, queryId: string, index: number) {
    for (let i = 0; i < productId?.length; i += chunkSize) {
      const chunk = productId?.slice(i, i + chunkSize);
      aa('clickedObjectIDsAfterSearch', {
        userToken: userToken?.replace(/^"|"$/g, ''),
        index: this?.index!,
        eventName: 'colour swatch clicked after search product',
        queryID: queryId,
        objectIDs: chunk,
        positions: [index],
      });
    }
  }
  handleColourSwatchesClickedInPLP(productId: string[], userToken: string) {
    for (let i = 0; i < productId?.length; i += chunkSize) {
      const chunk = productId?.slice(i, i + chunkSize);
      aa('clickedObjectIDs', {
        userToken: userToken?.replace(/^"|"$/g, ''),
        index: this?.index!,
        eventName: 'PLP colour swatch clicked',
        objectIDs: chunk,
      });
    }
  }

  handleClickLoadMore(productId: string[], userToken: string) {
    for (let i = 0; i < productId?.length; i += chunkSize) {
      const chunk = productId?.slice(i, i + chunkSize);
      aa('viewedObjectIDs', {
        userToken: userToken?.replace(/^"|"$/g, ''),
        index: this?.index!,
        eventName: 'Products Viewed on Load More',
        objectIDs: chunk,
      });
    }
  }

  handleSortClicked(filters: string[], userToken: string, eventName: string) {
    for (let i = 0; i < filters?.length; i += chunkSize) {
      const chunk = filters?.slice(i, i + chunkSize);
      aa('clickedFilters', {
        userToken: userToken?.replace(/^"|"$/g, ''),
        index: this?.index!,
        eventName: eventName,
        filters: chunk,
      });
    }
  }

  handleAddedToCartObjectIDs(objectID: string, userToken: string, eventName: string, queryID?: string | null) {
    if (!objectID || !queryID) return;
    aa('addedToCartObjectIDsAfterSearch', {
      userToken: userToken?.replace(/^"|"$/g, ''),
      eventName: eventName,
      index: this?.index!,
      objectIDs: [objectID],
      queryID: queryID ?? undefined,
    });
  }

  handleCategoryClicked(filters: string[], userToken: string) {
    aa('clickedFilters', {
      userToken: userToken?.replace(/^"|"$/g, ''),
      index: this?.index!,
      eventName: 'Search: Category Clicked',
      filters: filters,
    });
  }

  handleClickAddWishlist(productId: string, userToken: string, queryId: string) {
    aa('convertedObjectIDsAfterSearch', {
      userToken: userToken?.replace(/^"|"$/g, ''),
      index: this?.index!,
      eventName: 'SRP: Product Added to Wishlist',
      queryID: queryId,
      objectIDs: [productId],
    });
  }
  handlePLPViewedProduts(productId: string[], userToken: string, eventName: string) {
    for (let i = 0; i < productId?.length; i += chunkSize) {
      const chunk = productId?.slice(i, i + chunkSize);
      aa('viewedObjectIDs', {
        userToken: userToken?.replace(/^"|"$/g, ''),
        index: this?.index!,
        eventName: eventName,
        objectIDs: chunk,
      });
    }
  }

  handlePDPViewedProdut(productId: string, userToken: string) {
    aa('viewedObjectIDs', {
      userToken: userToken?.replace(/^"|"$/g, ''),
      index: this?.index!,
      eventName: 'PDP: Product Viewed',
      objectIDs: [productId],
    });
  }
  handleTrackCheckout(
    userToken: string,
    productIds: string[],
    objectData: any[],
    totalValue: number,
    currency: string,
  ) {
    const chunkSize = 20;
    const hasAllQueryIds = objectData.every(
      (item) =>
        item?.queryID !== undefined && item?.queryID !== 'null' && item?.queryID !== '' && item?.queryID !== null,
    );
    for (let i = 0; i < productIds.length; i += chunkSize) {
      const chunk = productIds.slice(i, i + chunkSize);
      const chunkData = objectData.slice(i, i + chunkSize);
      const method: AlgoliaInsightsMethod = hasAllQueryIds ? 'purchasedObjectIDsAfterSearch' : 'purchasedObjectIDs';
      aa(method, {
        userToken: userToken.replace(/^"|"$/g, ''),
        index: this.index!,
        eventName: 'Checkout',
        objectIDs: chunk,
        objectData: chunkData,
        value: +totalValue.toFixed(2),
        currency,
      });
    }
  }

  handleSearchPageFilterClick(userToken: string, filters: string[]) {
    for (let i = 0; i < filters?.length; i += chunkSize) {
      const chunk = filters?.slice(i, i + chunkSize);
      aa('clickedFilters', {
        userToken: userToken?.replace(/^"|"$/g, ''),
        index: this?.index!,
        eventName: 'SRP: Filter Clicked',
        filters: chunk,
      });
    }
  }
}
